import type { z } from 'zod';
import type { BootstrapData } from '@/types';
import { bootstrapDataSchema } from '@/lib/validation';

type ApiQueryParams = Record<string, string | number | boolean | null | undefined>;

interface ApiOptions<TResponse> extends RequestInit {
  params?: ApiQueryParams;
  responseSchema?: z.ZodType<TResponse>;
}

export class ApiError extends Error {
  status: number;
  data: unknown;

  constructor(message: string, status: number, data?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.data = data;
  }
}

export class ApiContractError extends Error {
  cause: unknown;

  constructor(cause: unknown) {
    super('API response did not match the expected shape.');
    this.name = 'ApiContractError';
    this.cause = cause;
  }
}

export function getBootstrap(): BootstrapData {
  const parsed = bootstrapDataSchema.safeParse(window.__LATINPLACE_BOOTSTRAP__);

  if (parsed.success) {
    return parsed.data;
  }

  return {
    apiBase: new URL('/wp-json/latinplace/v1/', window.location.origin).toString(),
    siteUrl: new URL('/', window.location.origin).toString(),
    themeUrl: new URL('/', window.location.origin).toString(),
    nonce: '',
    isLoggedIn: false,
    user: null,
  };
}

function buildUrl(endpoint: string, params?: ApiQueryParams): string {
  const bootstrap = getBootstrap();
  const base = bootstrap.apiBase.endsWith('/') ? bootstrap.apiBase : `${bootstrap.apiBase}/`;
  const url = new URL(endpoint.replace(/^\/+/, ''), base);

  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      if (value === null || typeof value === 'undefined') {
        return;
      }

      url.searchParams.set(key, String(value));
    });
  }

  return url.toString();
}

async function parseApiError(response: Response): Promise<ApiError> {
  let data: unknown;
  let message = `API error: ${response.status}`;

  try {
    data = await response.json();
    if (
      typeof data === 'object' &&
      data !== null &&
      'message' in data &&
      typeof (data as { message?: unknown }).message === 'string'
    ) {
      message = (data as { message: string }).message;
    }
  } catch {
    // Keep default message for non-JSON responses.
  }

  return new ApiError(message, response.status, data);
}

export async function api<TResponse>(endpoint: string, options: ApiOptions<TResponse> = {}): Promise<TResponse> {
  const { params, responseSchema, ...init } = options;
  const bootstrap = getBootstrap();
  const headers = new Headers(init.headers);

  if (bootstrap.nonce && !headers.has('X-WP-Nonce')) {
    headers.set('X-WP-Nonce', bootstrap.nonce);
  }

  if (init.body && !(init.body instanceof FormData) && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }

  const response = await fetch(buildUrl(endpoint, params), {
    credentials: 'same-origin',
    ...init,
    headers,
  });

  if (!response.ok) {
    throw await parseApiError(response);
  }

  if (response.status === 204) {
    return undefined as TResponse;
  }

  const data: unknown = await response.json();

  if (!responseSchema) {
    return data as TResponse;
  }

  const parsed = responseSchema.safeParse(data);
  if (!parsed.success) {
    throw new ApiContractError(parsed.error);
  }

  return parsed.data;
}
